import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { tokenFromHash } from "../lib/portal";
import { takeReturn } from "../lib/finalFeedback";

const LOGO_COLOR = "/assets/logo-fped-color.svg";

/**
 * `/portal/intrare#t=…` — where the link in the email lands.
 *
 * The link is spent by a click on this page, not by opening it. School mail
 * systems open every link to check it before the person does; if opening were
 * enough, a single-use link would arrive already used. And the token is wiped
 * from the address bar as soon as it is read, so it stays out of the history.
 */
export default function PortalEnter() {
  const navigate = useNavigate();
  const [token] = useState(() => tokenFromHash(typeof window !== "undefined" ? window.location.hash : ""));
  const [phase, setPhase] = useState(token ? "ready" : "failed");

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const enter = async () => {
    setPhase("entering");
    try {
      await api.post("/portal/auth/consume", { token });
      setPhase("in");
      // Signed in from the trainer's feedback link: back to it (30.10.2026).
      const back = takeReturn();
      setTimeout(() => navigate(back || "/portal", { replace: true }), 500);
    } catch {
      setPhase("failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F6F6] grid place-items-center px-4 sm:px-6 py-10" data-testid="portal-enter-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-[#E5E7EB] px-7 sm:px-10 py-10 shadow-sm flex flex-col items-center text-center">
        <img src={LOGO_COLOR} alt="Fundația pentru Educație Digitală" className="h-11 w-auto" />

        {(phase === "ready" || phase === "entering") && (
          <>
            <h1 className="mt-8 font-display text-2xl font-bold text-[#373A45]">Portalul cursului</h1>
            <p className="mt-2 text-sm text-[#6B7280]">Apasă ca să intri. Linkul merge o singură dată.</p>
            <button
              type="button" onClick={enter} disabled={phase === "entering"} autoFocus
              className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold py-3 transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#965EA7] focus-visible:ring-offset-2"
              data-testid="portal-enter"
            >
              {phase === "entering" && <Loader2 size={16} className="animate-spin" />}
              Intră în portal
            </button>
          </>
        )}

        {phase === "in" && (
          <>
            <div className="mt-8 h-12 w-12 rounded-2xl bg-[#519872] grid place-items-center text-white">
              <CheckCircle2 size={20} />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold text-[#373A45]">Ai intrat</h1>
          </>
        )}

        {phase === "failed" && (
          <div data-testid="portal-enter-failed">
            <div className="mt-8 mx-auto h-12 w-12 rounded-2xl bg-[#c0483f] grid place-items-center text-white">
              <ShieldAlert size={20} />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold text-[#373A45]">Linkul nu mai merge</h1>
            <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">
              Linkul a expirat sau a fost deja folosit: fiecare merge o singură dată. Cere unul nou, durează un minut.
            </p>
            <Link to="/portal" replace
              className="mt-8 w-full inline-flex items-center justify-center rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold py-3">
              Cere un link nou
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
